import { useEffect, useRef, useState } from 'react'
import { Box, TextField, ToggleButton, ToggleButtonGroup, Alert, Button, Typography } from '@mui/material'
import { StaticDateRangePicker, LocalizationProvider, CalendarPickerSkeleton, DatePicker } from '@mui/lab'
import { Download as DownloadIcon } from '@mui/icons-material'
import { isMobile } from 'react-device-detect'
import { useDispatch, useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { getData, setFeatureFlag } from '../../api/dataSlice'
import { useKeycloak } from '../Keycloak'
import { Accordion, AccordionSummary, AccordionDetails } from '../controls/Accordion'
import { setDownloadActions } from '../chatbot/ChatManager'
import { getPresetRanges, parseBotDate, rangeOverlaps } from '../Utilities'
import dayjs from 'dayjs'
import isBetween from 'dayjs/plugin/isBetween'
import DayjsAdapter from '@mui/lab/AdapterDayjs'
import i18next from 'i18next'
import CameraSelection from '../controls/CameraSelection'
import DownloadButton from '../controls/DownloadButton'

dayjs.extend(isBetween)

// gated by MYSITE or TIMELAPSE route access (role check skipped if either is `protected: false`), hidden on mobile (desktop only)
export default function DownloadAccordion({ expanded, onChangeExpanded, timestamp, shouldDisableDate, loading: isLoadingParent }) {

    const { t } = useTranslation()
    const { source, download, generation, siderWidth } = useSelector(getData)
    const { CAMERAS, LAYOUT: { TITLE, PROJECT_FILE_DATE, PROJECT_FINISHED }, BASEURL, ROUTES: { MYSITE, TIMELAPSE } } = window.conf
    const { hasRole, hasRouteAccess, isLoggedIn } = useKeycloak()

    const [forceRefresh, setForceRefresh] = useState(true)
    const [selectedCamera, setSelectedCamera] = useState(CAMERAS?.[0]?.id)
    const [dateRange, setDateRange] = useState([timestamp, timestamp])
    const [predefinedRange, setPredefinedRange] = useState("")
    const [alertMessage, setAlertMessage] = useState(null)
    const [startDateValid, setStartDateValid] = useState(true)
    const [endDateValid, setEndDateValid] = useState(true)
    const [mode, setMode] = useState("custom")
    
    const dispatch = useDispatch()
    const hasMysiteOrTimelapse = hasRouteAccess(MYSITE) || hasRouteAccess(TIMELAPSE)
    const daySize = 34
    const today = dayjs()
    const presetRanges = getPresetRanges(today)
    const refDownloadButton = useRef()
    const customRangeRef = useRef(false) // once true, the parent-timestamp sync effect leaves dateRange alone (user/chatbot took control)
    const isDownloadRangeExceeded = Math.abs(dateRange?.[0]?.diff(dateRange?.[1], 'day')) > 366
    const isFinished = PROJECT_FILE_DATE && PROJECT_FINISHED
    const minDate = dayjs(source?.[0]?.[0]?.[0])
    const maxDate = dayjs(source?.[0]?.[source[0].length - 1]?.[0])
    const moreThanOneCam = CAMERAS.length > 1
    const disableControls = isLoadingParent || download?.loading || generation?.loading || selectedCamera == null
    const disableDownloadTotalImages = !isFinished || !hasRole("download")
    const disableDownloadCustomImages = !source?.[0]?.length || selectedCamera == null || isDownloadRangeExceeded || !startDateValid || !endDateValid

    const camera = CAMERAS[selectedCamera]?.name
        ? `${CAMERAS[selectedCamera].name}`
        : moreThanOneCam
            ? `${t("events.camera")} ${CAMERAS[selectedCamera]?.id + 1}`
            : ""
    const title = TITLE ? (camera ? `${TITLE}_` : TITLE) : ""
    let startDate = dateRange?.[0]?.format("YY-MM-DD")
    const endDate = dateRange?.[1]?.format("YY-MM-DD")
    if (startDate && endDate && (startDate !== endDate)) startDate += "_" + endDate
    const targetFilenameCustom = `${title}${camera}${t("download.images")}_${startDate}`
    const stateRef = useRef({}) // chatbot registry mirror — assigned below, after onChange* handlers exist

    useEffect(() => {
        dispatch(setFeatureFlag({ name: 'hasDownload', value: !!hasMysiteOrTimelapse }))
    }, [dispatch, hasMysiteOrTimelapse])

    // sync the picker range with the parent-provided timestamp when it changes — but only until the user (or chatbot) has picked a custom range
    useEffect(() => {
        if (customRangeRef.current) return
        if (dateRange?.[0] !== timestamp || dateRange?.[1] !== timestamp) {
            setDateRange([timestamp, timestamp])
            setForceRefresh(!forceRefresh) // force remount of StaticDateRangePicker
        }
    // intentionally only react to `timestamp` changes from outside
    }, [timestamp]) // eslint-disable-line react-hooks/exhaustive-deps

    // scroll the download button into view when an alert appears so the user sees the cause next to it
    useEffect(() => {
        if (alertMessage) {
            refDownloadButton.current?.scrollIntoView({ behavior: "smooth", block: "center" })
        }
    }, [alertMessage])

    useEffect(() => {
        if (isDownloadRangeExceeded) {
            setAlertMessage({ type: "warning", message: t("msg.select_limitation") })
        }
    }, [isDownloadRangeExceeded]) // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (mode === "total" && !isFinished) {
            setAlertMessage({ type: "info", message: t("msg.after_finish") })
        }
    }, [mode, isFinished]) // eslint-disable-line react-hooks/exhaustive-deps

    function resetAlertMessage() {
        if (alertMessage) setAlertMessage(null)
    }

    const onChangeMode = (event, newValue) => {
        resetAlertMessage()
        if (newValue !== null) setMode(newValue)
    }

    const onChangeDateRange = (range) => {
        resetAlertMessage()
        if (range?.length) {
            setDateRange([range[0], range[1]])
            setPredefinedRange("")
            customRangeRef.current = true
        }
    }

    const onChangeStartDate = (start) => {
        resetAlertMessage()
        if (start?.isValid?.()) {
            setDateRange([start, dateRange[1]])
            setPredefinedRange("")
            customRangeRef.current = true
        }
    }

    const onChangeEndDate = (end) => {
        resetAlertMessage()
        if (end?.isValid?.()) {
            setDateRange([dateRange[0], end])
            setPredefinedRange("")
            customRangeRef.current = true
        }
    }

    const onChangePredefinedSpan = (event, newPredefinedSpan) => {
        resetAlertMessage()
        if (newPredefinedSpan && presetRanges[newPredefinedSpan]) {
            setDateRange(presetRanges[newPredefinedSpan])
            setPredefinedRange(newPredefinedSpan)
            setForceRefresh(!forceRefresh)
            customRangeRef.current = true
        }
    }

    // chatbot registry — refs feed live state and live setters
    stateRef.current = {
        selectedCamera, mode, dateRange, predefinedRange, startDateValid, endDateValid, alertMessage,
        setSelectedCamera, onChangeMode, onChangeDateRange, onChangePredefinedSpan, setForceRefresh,
    }

    useEffect(() => {
        setDownloadActions({
            getSelectedCameraId: () => stateRef.current.selectedCamera,
            getMode: () => stateRef.current.mode,
            getDateRange: () => {
                const [s, e] = stateRef.current.dateRange || []
                return (s?.isValid?.() && e?.isValid?.())
                    ? { start: s.format('YYYY-MM-DD'), end: e.format('YYYY-MM-DD') }
                    : null
            },
            getPredefinedRange: () => stateRef.current.predefinedRange || null,
            getStartDateValid: () => stateRef.current.startDateValid,
            getEndDateValid: () => stateRef.current.endDateValid,
            getAlertMessage: () => stateRef.current.alertMessage?.message || null,
            setSelectedCameraId: (id) => stateRef.current.setSelectedCamera(id),
            setMode: (newMode) => stateRef.current.onChangeMode(null, newMode),
            setDateRange: (start, end) => {
                const s = parseBotDate(start)
                const e = parseBotDate(end)
                if (s && e) {
                    stateRef.current.onChangeDateRange([s, e])
                    stateRef.current.setForceRefresh(prev => !prev) // remount StaticDateRangePicker so the calendar jumps to the new month
                }
            },
            // accepts 'day' | 'week' | 'month' — wrapper expands it into the matching range
            setPredefinedRange: (range) => stateRef.current.onChangePredefinedSpan(null, range),
        })
        return () => setDownloadActions(null)
    }, [])

    if (isMobile) return null

    return (
        <Accordion
            expanded={expanded === 'download'}
            onChange={onChangeExpanded('download')}
            disabled={!hasMysiteOrTimelapse}
            disabledTooltip={!isLoggedIn() ? t('msg.sign_in_to_unlock') : undefined}
        >
            <AccordionSummary className="accordion-with-subtitle">
                <Box>
                    <Typography>{t("download.images")}</Typography>
                    <Typography className="accordion-subtitle">{`${t("download.custom")} & ${t("download.total")}`}</Typography>
                </Box>
            </AccordionSummary>

            <AccordionDetails>
                <Box>
                    <ToggleButtonGroup
                        color="primary"
                        exclusive
                        onChange={onChangeMode}
                        value={mode}
                        fullWidth
                        sx={{ pb: 2 }}
                        disabled={disableControls}
                    >
                        <ToggleButton value="custom">{t("download.custom")}</ToggleButton>
                        <ToggleButton value="total">{t("download.total")}</ToggleButton>
                    </ToggleButtonGroup>

                    {moreThanOneCam && mode !== "total" && (
                        <CameraSelection
                            value={selectedCamera}
                            onChange={setSelectedCamera}
                            disabled={disableControls}
                        />
                    )}

                    {mode === "custom" && (
                        <Box>
                            <LocalizationProvider dateAdapter={DayjsAdapter} locale={i18next.language}>
                                <Box sx={{ display: 'flex' }}>
                                    <DatePicker
                                        label={t("date.start_date")}
                                        inputFormat={t("date.format.short")}
                                        mask={t("date.mask.short")}
                                        renderInput={props =>
                                            <TextField
                                                {...props}
                                                sx={{
                                                    width: "100%",
                                                    userSelect: "none",
                                                    "& .MuiOutlinedInput-root": { "& > fieldset": { borderColor: "#4caf50" } },
                                                    "& .MuiInputAdornment-root": { margin: '-4px' }
                                                }}
                                            />
                                        }
                                        onError={reason => setStartDateValid(!reason)}
                                        disableFuture={true}
                                        value={dateRange[0]}
                                        onChange={onChangeStartDate}
                                        cancelText={t("actions.cancel")}
                                        toolbarTitle={t("date.select_date")}
                                        minDate={minDate}
                                        maxDate={dateRange[1]}
                                        loading={isLoadingParent}
                                        disabled={disableControls}
                                        renderLoading={() => <CalendarPickerSkeleton />}
                                    />

                                    <p style={{
                                        paddingLeft: 12,
                                        paddingRight: 12,
                                        height: "100%",
                                        alignItems: 'center',
                                        userSelect: 'none'
                                    }}>
                                        {t("date.to")}
                                    </p>

                                    <DatePicker
                                        label={t("date.end_date")}
                                        inputFormat={t("date.format.short")}
                                        mask={t("date.mask.short")}
                                        renderInput={props =>
                                            <TextField
                                                {...props}
                                                sx={{
                                                    width: "100%",
                                                    userSelect: "none",
                                                    "& .MuiOutlinedInput-root": { "& > fieldset": { borderColor: "#ed6c02" } },
                                                    "& .MuiInputAdornment-root": { margin: '-4px' }
                                                }}
                                            />
                                        }
                                        onError={reason => setEndDateValid(!reason)}
                                        disableFuture={true}
                                        value={dateRange[1]}
                                        onChange={onChangeEndDate}
                                        cancelText={t("actions.cancel")}
                                        toolbarTitle={t("date.select_date")}
                                        minDate={dateRange[0]}
                                        maxDate={maxDate}
                                        loading={isLoadingParent}
                                        disabled={disableControls}
                                        renderLoading={() => <CalendarPickerSkeleton />}
                                    />
                                </Box>
                            </LocalizationProvider>

                            <Box
                                sx={{
                                    "& > div": { minWidth: siderWidth, pt: 2 },
                                    "& > div > div": {
                                        border: "1px solid",
                                        borderColor: "#BDBDBD",
                                        borderRadius: "4px"
                                    },
                                    "& > div > div, & > div > div > div, & > div > div > div > div": {
                                        width: siderWidth - 2
                                    },
                                    "& .MuiTypography-caption": { width: daySize + 4, margin: 0 },
                                    "& .PrivatePickersSlideTransition-root": {
                                        minHeight: daySize * 7.2,
                                        overflow: "hidden"
                                    },
                                    '& .PrivatePickersSlideTransition-root [role="grid"]': { width: siderWidth },
                                    "& .MuiPickersDay-root": { width: daySize, height: daySize }
                                }}
                            >
                                <LocalizationProvider dateAdapter={DayjsAdapter} locale={i18next.language} key={forceRefresh}>
                                    <StaticDateRangePicker
                                        calendars={1}
                                        displayStaticWrapperAs="desktop"
                                        mask={t("date.mask.full")}
                                        value={dateRange}
                                        onChange={onChangeDateRange}
                                        renderInput={() => {}}
                                        minDate={minDate}
                                        maxDate={maxDate}
                                        loading={isLoadingParent}
                                        disabled={disableControls}
                                        shouldDisableDate={shouldDisableDate}
                                        renderLoading={() => <CalendarPickerSkeleton />}
                                    />
                                </LocalizationProvider>
                            </Box>

                            <ToggleButtonGroup
                                color="primary"
                                exclusive
                                onChange={onChangePredefinedSpan}
                                value={disableControls ? '' : predefinedRange}
                                fullWidth
                                orientation='vertical'
                                sx={{ py: 2 }}
                            >
                                <ToggleButton disabled={disableControls || !rangeOverlaps(presetRanges.day[0], presetRanges.day[1], minDate, maxDate)} value="day">{t("date.yesterday")}</ToggleButton>
                                <ToggleButton disabled={disableControls || !rangeOverlaps(presetRanges.week[0], presetRanges.week[1], minDate, maxDate)} value="week">{t("date.last_week")}</ToggleButton>
                                <ToggleButton disabled={disableControls || !rangeOverlaps(presetRanges.month[0], presetRanges.month[1], minDate, maxDate)} value="month">{t("date.last_month")}</ToggleButton>
                            </ToggleButtonGroup>
                        </Box>
                    )}

                    {alertMessage && (
                        <Box sx={{ mb: 2 }}>
                            <Alert
                                variant="filled"
                                severity={alertMessage.type}
                                sx={{ alignItems: "center", userSelect: "none" }}
                            >
                                {alertMessage.message || t("msg.unknown_error")}
                            </Alert>
                        </Box>
                    )}

                    {mode === "total" && (
                        <Button
                            component="a"
                            href={`${BASEURL}downloads/${PROJECT_FILE_DATE ^ 9102015}.zip`}
                            download={`${dayjs.unix(PROJECT_FILE_DATE).format("YY-MM-DD")}.zip`}
                            target="_blank"
                            rel="noopener"
                            variant="contained"
                            size="large"
                            startIcon={<DownloadIcon />}
                            sx={{ width: "100%" }}
                            disabled={disableControls || disableDownloadTotalImages}
                            onClick={event => (disableControls || disableDownloadTotalImages) && event.preventDefault()}
                        >
                            {t("actions.download")}
                        </Button>
                    )}

                    {mode === "custom" && (
                        <DownloadButton
                            targetFilename={targetFilenameCustom}
                            disabled={disableControls || disableDownloadCustomImages}
                            selectedCamera={selectedCamera}
                            dateRange={dateRange}
                            setAlertMessage={setAlertMessage}
                            ref={refDownloadButton}
                        />
                    )}
                </Box>
            </AccordionDetails>
        </Accordion>
    )
}
